import { useEffect, useMemo, useState } from 'react'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { isEmpty } from 'lodash'
// components
import { Divider } from '@/components/page/Divider/Divider'
import { ProofWatermark } from '@/components/printPage/ProofWatermark'
import * as S from '@/components/printPage/Page.styled'
import * as Items from '@/components/page/Items'
// atoms
import { selectedThemeAtom } from '@/atoms/printPackageAtoms'
import { themeIdAtom } from '@/atoms/editorAtoms'
import { caseAtom } from '@/atoms/caseAtoms'
import { caseImagesAtom } from '@/atoms/caseImageAtoms'
// hooks
import { useFillCaseImages } from '@/hooks/editor/useFillCaseImages'
// utils
import { normalizeBackgroundLayout } from '@/utils/editor/backgroundUtils'
import { convertKeysToSnakeCase } from '@/utils/editor/camelToSnakeCase'
import { transformToPascalCase } from '@/utils/global/transformToPascalCase'
import { isItemOutsidePageBoundaries } from '@/utils/editor/isItemOutsidePageBoundaries'
import { layoutMarginsOf } from '@/utils/editor/layoutMargins'
import { previewBleedReportOf } from '@/utils/editor/previewBleedReport'
import { useEditorPageContent } from '@/hooks/editor/useEditorPageContent'
import {
  hydrateEditorTextBoxItems,
  isEditorTextBoxItem,
} from '@/utils/editor-text-box/editorTextBoxAdapter'

export const ProductPrint = ({
  children,
  page: { product, pageProperties },
  theme,
  themeOverride,
  //do not remove this padding, it is used for the page print
  // eslint-disable-next-line
  padding,
  isPMRegisterBook,
  isEven,
  isPDF,
  pageTwo,
  isProof,
  environment,
  backgroundType,
  orderId,
  pageSetId,
  orderPageSetId,
  pagePosition,
  baseLayout,
  clipBleed,
  clipPath,
  nonClippedProducts,
  transparentBackground = false,
  ...props
}) => {
  const _case = useRecoilValue(caseAtom)
  const caseImages = useRecoilValue(caseImagesAtom)
  const themeId = useRecoilValue(themeIdAtom)
  const setSelectedTheme = useSetRecoilState(selectedThemeAtom)
  const fillCaseImages = useFillCaseImages()
  const isBackgroundPreprinted = backgroundType === 'PREPRINTED'
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(true)
  const editorPageContent = useEditorPageContent(product?.orderPageId, {
    fetch: !isPDF,
  })
  const printItems = useMemo(
    () => hydrateEditorTextBoxItems(product?.items ?? [], editorPageContent),
    [product?.items, editorPageContent],
  )

  // The page the load rule reads, on both read-only surfaces: the hydrated
  // items (a stored Lotus item is `{id, type}` alone, so without the merge a
  // Lotus box would bound nothing), the layout's margins, the page size and
  // its padding. The canvas builds the same thing out of the editor's atoms,
  // so the three surfaces land on the same frame. No layout is no margins,
  // never a crash.
  const pageGeometry = useMemo(
    () => ({
      items: printItems,
      margins: layoutMarginsOf(baseLayout),
      pageWidth: product?.productSize?.width ?? 0,
      pageHeight: product?.productSize?.height ?? 0,
      padding: product?.padding ?? null,
      // The PDF paints no hidden item, so none of them bounds a box there.
      dropsHiddenInPdf: isPDF === true,
    }),
    [
      printItems,
      baseLayout,
      product?.productSize?.width,
      product?.productSize?.height,
      product?.padding,
      isPDF,
    ],
  )

  // On the preview a settled Lotus box judges its ink against the page's
  // margins and logs a crossing with the ids that name the page; the PDF job
  // does the same from its own snapshot, so the surface here is preview only.
  const bleedReport = useMemo(() => {
    if (environment !== 'preview') return null
    return previewBleedReportOf({
      layout: baseLayout,
      pageWidth: product?.productSize?.width ?? 0,
      pageHeight: product?.productSize?.height ?? 0,
      padding: product?.padding ?? null,
      storedPageNumber: pageProperties?.pageNumber,
      pagePosition,
      orderPageId: product?.orderPageId ?? null,
      orderId: orderId ?? null,
      orderPageSetId: orderPageSetId ?? null,
      pageSetId: pageSetId ?? null,
    })
  }, [
    environment,
    baseLayout,
    product?.productSize?.width,
    product?.productSize?.height,
    product?.padding,
    product?.orderPageId,
    pageProperties?.pageNumber,
    pagePosition,
    orderId,
    orderPageSetId,
    pageSetId,
  ])

  // Normalized theme background layout used as the base for every page render.
  const normalizedThemeLayout = useMemo(() => {
    return normalizeBackgroundLayout(themeOverride, null)
  }, [themeOverride])

  // Normalized background layout that merges page overrides when they match the active theme.
  const pageLayout = useMemo(() => {
    const pageOverrideSnakeCase = convertKeysToSnakeCase(product?.themeOverride)
    const pageOverrideIsMissing = !pageOverrideSnakeCase
    const themeIdToMatch = themeId || themeOverride?.theme_id
    const pageOverrideMatchesTheme =
      pageOverrideSnakeCase?.theme_id === themeIdToMatch

    if (!pageOverrideIsMissing && !pageOverrideMatchesTheme) {
      return {
        ...normalizedThemeLayout,
        theme_id: themeOverride?.theme_id,
      }
    }

    if (!pageOverrideIsMissing && pageOverrideMatchesTheme) {
      return normalizeBackgroundLayout(themeOverride, pageOverrideSnakeCase)
    }

    return normalizedThemeLayout
  }, [product?.themeOverride, themeId, themeOverride, normalizedThemeLayout])

  useEffect(() => {
    if (_case.id && !_case?.filledImages) {
      fillCaseImages()
    }
    if (!isEmpty(theme)) {
      setSelectedTheme(theme)
    }
    //eslint-disable-next-line
  }, [_case])

  useEffect(() => {
    if (isPDF) return
    if (_case?.id) {
      fillCaseImages()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseImages, isPDF])

  useEffect(() => {
    // Trigger 12px bleed clipping for PDF proof rendering (exclude specific products)
    const productName = pageProperties?.product
    const shouldClipBleed =
      clipBleed &&
      isProof &&
      isPDF &&
      !nonClippedProducts?.includes?.(productName)
    if (shouldClipBleed) {
      clipBleed()
    }
  }, [pageProperties?.product, clipBleed, isProof, isPDF, nonClippedProducts])

  const editedImageMatchesTheme =
    themeOverride?.theme_id === pageLayout?.background_edits?.theme_id

  // background image url logic
  const backgroundImageUrl =
    pageLayout?.background_edits?.url && editedImageMatchesTheme
      ? pageLayout?.background_edits?.url
      : `${process.env.REACT_APP_S3BUCKET_THEME_IMAGES}${
          themeOverride?.replacement_image
            ? themeOverride?.replacement_image
            : theme?.image
        }`

  const handleBackgroundLoad = () => {
    setIsBackgroundLoading(false)
  }

  const handleBackgroundError = () => {
    setIsBackgroundLoading(false)
    // supress warning for now, seems to pop up even if background image is loaded
    // TODO: fix this, investigate why this is happening
    // const errorMessage = `Background image failed to load: ${backgroundImageUrl}`
    // handleError(errorMessage, new Error(errorMessage))
  }

  const IsItemInCanvas = (item) => {
    return isItemOutsidePageBoundaries(
      item.x,
      item.y,
      typeof item.width === 'string' ? parseInt(item.width) : item.width,
      typeof item.height === 'string' ? parseInt(item.height) : item.height,
      product?.productSize,
      product?.padding,
      product?.whiteBorder,
    )
  }

  return (
    <>
      <S.ProductPrint
        height={`${product?.productSize?.height}px`}
        width={`${product?.productSize?.width}px`}
        padding={product?.padding}
        whiteBorder={product?.whiteBorder}
        theme={theme}
        pageLayout={pageLayout}
        themeOverride={themeOverride}
        backgroundImageUrl={backgroundImageUrl}
        isBackgroundPreprinted={isBackgroundPreprinted}
        pageTwo={pageTwo}
        paperType={pageProperties?.paperType}
        data-background-loading={isBackgroundLoading}
        clipPath={clipPath}
        $transparentBackground={transparentBackground}
        {...props}
        data-page-surface={environment}
        data-page-id={product?.orderPageId}
      >
        {isProof && <ProofWatermark />}
        {!isPDF &&
          product?.noPrintAreas?.map((noPrint) => (
            <S.NoPrint
              key={noPrint.id}
              left={`${noPrint.x}px`}
              top={`${noPrint.y}px`}
              height={`${noPrint.height}px`}
              width={`${noPrint.width}px`}
            />
          ))}
        {isPMRegisterBook && <S.RBDiv product={product} isEven={isEven} />}
        {product?.dividers?.map(
          (divider, index) =>
            divider?.whiteBorder && (
              <Divider
                key={index}
                left={divider?.left}
                whiteBorder={divider?.whiteBorder}
                padding={product?.padding}
              />
            ),
        )}
        <S.RelativeWrapper>
          <S.PerfGuide
            height={product?.productSize?.height}
            width={product?.productSize?.width}
            padding={product?.padding}
            whiteBorder={product?.whiteBorder}
            overflow="hidden"
          >
            {product?.watermarks?.map((item, i) => {
              const Item =
                Items[`Page${transformToPascalCase(item.type)}`] ||
                Items.PageShape

              const isItemOutside = IsItemInCanvas(item)

              if (isItemOutside) {
                return null
              }

              return (
                <Item
                  {...item}
                  key={i}
                  {...props}
                  print
                  environment={environment}
                  images={_case?.filledImages}
                  orderId={orderId}
                  pageSetId={pageSetId}
                />
              )
            })}
          </S.PerfGuide>

          {printItems.map((item, i) => {
            const Item =
              Items[`Page${transformToPascalCase(item.type)}`] ||
              Items.PageShape

            const isItemOutside = IsItemInCanvas(item)

            if (isItemOutside) {
              return null
            }

            if (isPDF && item.hideInPdf) {
              return null
            }

            return (
              <Item
                {...item}
                key={i + item?.id}
                {...props}
                print
                environment={environment}
                images={_case?.filledImages}
                {...(isEditorTextBoxItem(item)
                  ? { editorPageId: product?.orderPageId }
                  : {})}
                bleedReport={bleedReport}
                pageGeometry={pageGeometry}
                resizingState="idle"
                orderId={orderId}
                pageSetId={pageSetId}
              />
            )
          })}
          {children}
        </S.RelativeWrapper>
      </S.ProductPrint>
      {/* Hidden image to track loading status of the background */}
      <img
        src={backgroundImageUrl}
        onLoad={handleBackgroundLoad}
        onError={handleBackgroundError}
        style={{ display: 'none' }}
        alt=""
      />
    </>
  )
}

export default ProductPrint
